import React, { useState, useEffect, useRef } from 'react';
import { SKILL_LP_OPTIONS } from '../generate-core-lesson-plan/generate-core-lesson-plan';
import styled from '@emotion/styled';
import { LoadingScreen } from '../../../molecules/loading-screen/loading-screen';
import { ChipFilter } from '../../../atoms/chip/chip-filter';
import { CoreLessonPlanTextEditor } from '../../../molecules/core-lesson-plan-text-editor/core-lesson-plan-text-editor';
import { LpRecordingIdleStickyFooter } from '../../lesson-plan/shared/lp-recording-idle-sticky-footer';
import { LpRecordingStickyFooter } from '../../lesson-plan/shared/lp-recording-sticky-footer';
import { LpRecordingToast } from '../../lesson-plan/shared/lp-recording-toast';
import { RecordingActionDialogs } from '../../lesson-plan/shared/recording-action-dialogs';
import {
  LessonPlanFeedbackDialog,
  LessonPlanFeedbackData,
} from './lesson-plan-feedback-dialog';
import { AiEditDialog } from './ai-edit-dialog';
import {
  LessonPlanHistoryDialog,
  LessonPlanHistoryEntry,
} from './lesson-plan-history-dialog';
import {
  COACHING_RECORDING_CONFIG,
  useNetworkStatus,
  AudioRecorderStatus,
  getExitDialogContent,
} from '@taleemabad/shared';
import { JSONContent } from '@tiptap/react';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Button,
  Icon,
  IconButton,
  Progress,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';

enum LessonPlanLanguage {
  English = 'english',
  Urdu = 'urdu',
}

export type AiEditStatusType =
  | 'idle'
  | 'submitting'
  | 'pending'
  | 'ready'
  | 'error';

export interface ICoreLessonPlanDetail {
  id: string;
  title: string;
  subject: string;
  grade: string;
  topic: string;
  classStrength: number;
  content: string;
  /** Optional content for bilingual support (e.g., Urdu translation) */
  contentBilingual?: string | null;
  createdAt: string;
  updatedAt: string;
  gradeSubjectId?: number;
  /** User's previous rating for this lesson plan (1-5) */
  rating?: number | null;
  /** User's previous feedback for this lesson plan */
  feedback?: string | null;
  /** Timestamp when the lesson plan was marked as completed */
  completedAt?: string | null;
  /** Skill-based LP type (e.g., "reading", "grammar", "concrete") */
  lpType?: string | null;
}

export interface CoreLessonPlanViewerProps {
  lessonPlan: ICoreLessonPlanDetail;
  isLoading?: boolean;
  isSaving?: boolean;
  onEdit?: () => void;
  onSave?: (html: string) => void;
  onGenerateNew?: () => void;
  onLessonComplete?: (feedbackData?: LessonPlanFeedbackData) => void;
  onContentChange?: (html: string) => void;
  // Recording Props
  isRecordingEnabled?: boolean;
  recordingState?: AudioRecorderStatus;
  elapsedTime?: number;
  elapsedLabel?: string;
  onRecordStart?: () => void;
  onRestart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  showExitConfirmation?: boolean;
  onExitConfirm?: () => void;
  onExitCancel?: () => void;
  showToast?: boolean;
  onCloseToast?: () => void;
  durationLimitReached?: boolean;
  // Existing recording props (resume flow)
  hasExistingRecording?: boolean;
  existingRecordingDuration?: string;
  onSubmitExistingRecording?: () => void;
  // AI Edit Props
  isAiEditEnabled?: boolean;
  aiEditStatus?: AiEditStatusType;
  aiEditsRemaining?: number;
  onAiEditSubmit?: (instruction: string) => void;
  onEditYourself?: () => void;
  // History Props
  isHistoryEnabled?: boolean;
  historyEntries?: LessonPlanHistoryEntry[];
  isHistoryLoading?: boolean;
  onHistoryOpen?: () => void;
  onHistoryVersionSelect?: (entry: LessonPlanHistoryEntry) => void;
  viewingHistoryId?: number | null;
  // Version label shown above content
  versionLabel?: string;
  versionIsLatest?: boolean;
}

export const CoreLessonPlanViewer: React.FC<CoreLessonPlanViewerProps> = ({
  lessonPlan,
  isLoading = false,
  isSaving = false,
  onEdit,
  onSave,
  onGenerateNew,
  onLessonComplete,
  onContentChange,
  isRecordingEnabled = false,
  recordingState = 'idle',
  elapsedTime = 0,
  elapsedLabel = '00:00.0',
  onRecordStart,
  onRestart,
  onPause,
  onResume,
  showExitConfirmation = false,
  onExitConfirm,
  onExitCancel,
  showToast = false,
  onCloseToast,
  durationLimitReached = false,
  hasExistingRecording = false,
  existingRecordingDuration = '00:00.0',
  onSubmitExistingRecording,
  // AI Edit Props
  isAiEditEnabled = false,
  aiEditStatus = 'idle',
  aiEditsRemaining = 0,
  onAiEditSubmit,
  onEditYourself,
  // History Props
  isHistoryEnabled = false,
  historyEntries = [],
  isHistoryLoading = false,
  onHistoryOpen,
  onHistoryVersionSelect,
  viewingHistoryId,
  versionLabel,
  versionIsLatest = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedHTML, setEditedHTML] = useState<string | null>(null);
  const [shouldFocusEditor, setShouldFocusEditor] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showRestartConfirmation, setShowRestartConfirmation] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [showAiEditDialog, setShowAiEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LessonPlanLanguage>(
    LessonPlanLanguage.English,
  );
  const editMenuRef = useRef<HTMLDivElement>(null);

  const { isConnected } = useNetworkStatus();
  const isOffline = !isConnected;

  const isAiProcessing =
    aiEditStatus === 'submitting' || aiEditStatus === 'pending';
  // Dismiss the internal submit confirmation when the recording limit fires —
  // prevents two dialogs stacking (DSM submit + outer limit dialog).
  useEffect(() => {
    if (durationLimitReached && showSubmitPopup) {
      setShowSubmitPopup(false);
    }
  }, [durationLimitReached, showSubmitPopup]);

  useEffect(() => {
    if (isEditing) {
      const timeoutId = setTimeout(() => {
        setShouldFocusEditor(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isEditing]);

  // Close edit menu when clicking outside
  useEffect(() => {
    if (!showEditMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        editMenuRef.current &&
        !editMenuRef.current.contains(event.target as Node)
      ) {
        setShowEditMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEditMenu]);

  const handleEditYourself = () => {
    if (isOffline) return;
    setShowEditMenu(false);
    setIsEditing(true);
    onEditYourself?.();
    onEdit?.();
  };

  const handleAiEditClick = () => {
    setShowEditMenu(false);
    setShowAiEditDialog(true);
  };

  const handleAiEditSubmit = (instruction: string) => {
    setShowAiEditDialog(false);
    onAiEditSubmit?.(instruction);
  };

  const handleHistoryOpen = () => {
    onHistoryOpen?.();
    setShowHistoryDialog(true);
  };

  const handleHistoryVersionSelect = (entry: LessonPlanHistoryEntry) => {
    setShowHistoryDialog(false);
    onHistoryVersionSelect?.(entry);
  };

  const handleFocusComplete = () => {
    setShouldFocusEditor(false);
  };

  const handleSave = () => {
    if (isOffline) {
      return;
    }

    if (editedHTML) {
      onSave?.(editedHTML);
      setIsEditing(false);
      setEditedHTML(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHTML(null);
  };

  const handleContentChange = (_content: JSONContent, html: string) => {
    setEditedHTML(html);
    onContentChange?.(html);
  };

  const getContentToShow = (): string => {
    if (isEditing && editedHTML) {
      return editedHTML;
    }

    if (
      selectedLanguage === LessonPlanLanguage.Urdu &&
      lessonPlan.contentBilingual
    ) {
      return lessonPlan.contentBilingual;
    }

    return lessonPlan.content;
  };

  const handleLessonCompleteClick = () => {
    if (recordingState !== 'idle') {
      if (elapsedTime < COACHING_RECORDING_CONFIG.MIN_DURATION_MS) {
        setShowFeedbackDialog(true);
      } else {
        setShowSubmitPopup(true);
      }
    } else {
      setShowFeedbackDialog(true);
    }
  };

  const handleConfirmSubmit = () => {
    setShowSubmitPopup(false);
    setShowFeedbackDialog(true);
  };

  const handleFeedbackSubmit = (feedbackData: LessonPlanFeedbackData) => {
    setShowFeedbackDialog(false);
    onLessonComplete?.(feedbackData);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackDialog(false);
    // Still complete the lesson even if user skips feedback
    onLessonComplete?.();
  };

  const isEditDisabled =
    isOffline ||
    selectedLanguage === LessonPlanLanguage.Urdu ||
    recordingState !== 'idle' ||
    isAiProcessing;

  if (isLoading || isAiProcessing) {
    return (
      <LoadingScreen
        heading="Loading Lesson Plan"
        message="Please wait while the lesson plan is being loaded..."
      />
    );
  }

  if (!lessonPlan) {
    return (
      <Container>
        <StyledDiv flexDirection="column" alignItem="center">
          <Typography variant="textHeadlineMedium">
            No Lesson Plan Available
          </Typography>
          <Typography variant="textBodyLarge">
            The requested lesson plan could not be found.
          </Typography>
          {onGenerateNew && (
            <Spacing marginTop="lg">
              <Button variant="filled" onClick={onGenerateNew}>
                Generate New Lesson Plan
              </Button>
            </Spacing>
          )}
        </StyledDiv>
      </Container>
    );
  }

  return (
    <>
      <LessonPlanFeedbackDialog
        open={showFeedbackDialog}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
        initialRating={lessonPlan.rating}
        initialFeedback={lessonPlan.feedback}
        isSubmitting={isSaving}
      />
      <AiEditDialog
        open={showAiEditDialog}
        onClose={() => setShowAiEditDialog(false)}
        onSubmit={handleAiEditSubmit}
        aiEditsRemaining={aiEditsRemaining}
      />
      <LessonPlanHistoryDialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        entries={historyEntries}
        isLoading={isHistoryLoading}
        onSelectVersion={handleHistoryVersionSelect}
        viewingHistoryId={viewingHistoryId}
      />
      <RecordingActionDialogs
        showSubmitPopup={showSubmitPopup}
        onCloseSubmitPopup={() => setShowSubmitPopup(false)}
        onConfirmSubmit={handleConfirmSubmit}
        showRestartPopup={showRestartConfirmation}
        onCloseRestartPopup={() => setShowRestartConfirmation(false)}
        onConfirmRestart={() => {
          onRestart?.();
          setShowRestartConfirmation(false);
        }}
        showExitPopup={showExitConfirmation}
        onCloseExitPopup={() => onExitCancel?.()}
        onConfirmExit={() => onExitConfirm?.()}
        exitTitle={getExitDialogContent(elapsedTime).title}
        exitDescription={getExitDialogContent(elapsedTime).description}
      />
      <Container>
        <LpRecordingToast
          message="Recording has started"
          show={showToast}
          onClose={() => onCloseToast?.()}
        />

        <HeaderContainer>
          <StyledDiv justifyContent="space-between" alignItem="flex-start">
            <StyledDiv flexDirection="column" width="100%">
              <Spacing paddingBottom="sm">
                <Typography inline variant="textBodySmall">
                  Lesson Plan ID: {lessonPlan.id}
                </Typography>
              </Spacing>
              <Spacing paddingBottom="lg">
                <Typography variant="textDisplaySmall">
                  {lessonPlan.title}
                </Typography>
              </Spacing>

              <MetadataContainer>
                <StyledDiv flexDirection="column" gap={4}>
                  <Typography variant="textBodySmall">Subject</Typography>
                  <Typography variant="textBodyLarge">
                    {lessonPlan.subject}
                  </Typography>
                </StyledDiv>
                <StyledDiv flexDirection="column" gap={4}>
                  <Typography variant="textBodySmall">Grade</Typography>
                  <Typography variant="textBodyLarge">
                    {lessonPlan.grade}
                  </Typography>
                </StyledDiv>
                <StyledDiv flexDirection="column" gap={4}>
                  <Typography variant="textBodySmall">
                    Class Strength
                  </Typography>
                  <Typography variant="textBodyLarge">
                    {lessonPlan.classStrength} students
                  </Typography>
                </StyledDiv>
                {lessonPlan.topic?.trim() && (
                  <StyledDiv flexDirection="column" gap={4}>
                    <Typography variant="textBodySmall">Topic</Typography>
                    <Typography variant="textBodyLarge">
                      {lessonPlan.topic}
                    </Typography>
                  </StyledDiv>
                )}
                {lessonPlan.lpType && (() => {
                  const allOptions = Object.values(SKILL_LP_OPTIONS).flat();
                  const match = allOptions.find(o => o.value === lessonPlan.lpType);
                  return match ? (
                    <StyledDiv flexDirection="column" gap={4}>
                      <Typography variant="textBodySmall">Type</Typography>
                      <Typography variant="textBodyLarge">
                        {match.content}
                      </Typography>
                    </StyledDiv>
                  ) : null;
                })()}
              </MetadataContainer>
            </StyledDiv>
            {isHistoryEnabled && versionLabel && (
              <VersionBanner
                $isLatest={versionIsLatest}
                onClick={handleHistoryOpen}
                data-cy="version-banner"
              >
                <Typography
                  variant="textLabelLarge"
                  color="var(--md-sys-color-on-surface)"
                >
                  Version: {versionLabel}
                </Typography>
                <Icon
                  name="expand_more"
                  size="regular"
                  color="--md-sys-color-on-surface"
                />
              </VersionBanner>
            )}
          </StyledDiv>
        </HeaderContainer>
        {lessonPlan.contentBilingual && (
          <TranslateContainer>
            <Typography variant="textBodyMedium" textAlign="center">
              Translate In:
            </Typography>
            <ChipFilter
              label={'English'}
              selected={selectedLanguage === LessonPlanLanguage.English}
              onClick={() => {
                setSelectedLanguage(LessonPlanLanguage.English);
              }}
            />
            <ChipFilter
              label={'Urdu'}
              selected={selectedLanguage === LessonPlanLanguage.Urdu}
              disabled={!lessonPlan.contentBilingual}
              onClick={() => {
                if (lessonPlan.contentBilingual) {
                  setSelectedLanguage(LessonPlanLanguage.Urdu);
                }
              }}
            />
          </TranslateContainer>
        )}
        <ContentWrapper>
          {isAiProcessing && (
            <AiProcessingOverlay data-cy="ai-processing-overlay">
              <Progress type="circular" indeterminate size="large" />
              <Spacing marginTop="lg">
                <Typography
                  variant="textTitleMedium"
                  color="var(--md-sys-color-on-surface)"
                >
                  AI is updating your lesson plan...
                </Typography>
              </Spacing>
              <Spacing marginTop="sm">
                <Typography
                  variant="textBodySmall"
                  color="var(--md-sys-color-outline)"
                >
                  This may take a moment. Please don't navigate away.
                </Typography>
              </Spacing>
            </AiProcessingOverlay>
          )}
          <ContentContainer $dimmed={isAiProcessing}>
            <CoreLessonPlanTextEditor
              content={getContentToShow()}
              isEditable={isEditing}
              onContentChange={handleContentChange}
              shouldFocusAtEnd={shouldFocusEditor}
              onFocusComplete={handleFocusComplete}
            />
          </ContentContainer>
        </ContentWrapper>

        <ActionButtonsResponsive
          justifyContent="center"
          gap={16}
          marginTop={24}
        >
          {isEditing ? (
            <>
              <Button
                variant="filled"
                onClick={handleSave}
                disabled={isSaving || isOffline}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {onLessonComplete && (
                <Button
                  variant="filled"
                  onClick={handleLessonCompleteClick}
                  disabled={isOffline || isAiProcessing}
                >
                  {recordingState !== 'idle'
                    ? 'Complete Lesson & Submit Recording'
                    : lessonPlan.completedAt
                      ? 'Update Feedback'
                      : 'Complete Lesson'}
                </Button>
              )}
              {onEdit && !lessonPlan.completedAt && (
                <EditButtonWrapper ref={editMenuRef}>
                  {isAiEditEnabled && !isOffline ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => setShowEditMenu(!showEditMenu)}
                        disabled={isEditDisabled}
                        data-cy="edit-dropdown-button"
                      >
                        <StyledDiv alignItem="center" gap={4}>
                          Edit
                          <Icon name="arrow_drop_down" size="tiny" />
                        </StyledDiv>
                      </Button>
                      {showEditMenu && (
                        <EditMenu data-cy="edit-dropdown-menu">
                          <EditMenuItem
                            onClick={handleEditYourself}
                            data-cy="edit-yourself-option"
                          >
                            <Icon
                              name="edit"
                              size="tiny"
                              color="--md-sys-color-on-surface"
                            />
                            <Typography variant="textBodyMedium">
                              Edit Yourself
                            </Typography>
                          </EditMenuItem>
                          {aiEditsRemaining > 0 && (
                            <EditMenuItem
                              onClick={handleAiEditClick}
                              data-cy="edit-with-ai-option"
                            >
                              <Icon
                                name="auto_awesome"
                                size="tiny"
                                color="--md-sys-color-on-surface"
                              />
                              <Typography variant="textBodyMedium">
                                Edit with AI
                              </Typography>
                            </EditMenuItem>
                          )}
                        </EditMenu>
                      )}
                      {aiEditsRemaining > 0 && !isAiProcessing && (
                        <Spacing marginTop="sm">
                          <Typography
                            variant="textBodySmall"
                            color="var(--md-sys-color-primary)"
                            textAlign="center"
                          >
                            Try editing with AI
                          </Typography>
                        </Spacing>
                      )}
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleEditYourself}
                      disabled={isEditDisabled}
                    >
                      Edit
                    </Button>
                  )}
                </EditButtonWrapper>
              )}
              {onGenerateNew && (
                <Button
                  variant="outlined"
                  onClick={onGenerateNew}
                  disabled={recordingState !== 'idle' || isAiProcessing}
                >
                  {lessonPlan.completedAt ? 'Generate new Lesson Plan' : 'Generate Lesson Plan'}
                </Button>
              )}
            </>
          )}
        </ActionButtonsResponsive>

        {isOffline && (
          <Spacing marginTop="md">
            <StyledDiv justifyContent="center">
              <Typography
                variant="textBodySmall"
                color="var(--md-sys-color-outline)"
                textAlign="center"
              >
                You are currently offline. Editing and completing lessons is
                disabled until you reconnect.
              </Typography>
            </StyledDiv>
          </Spacing>
        )}

        {(recordingState !== 'idle' || durationLimitReached) &&
          isRecordingEnabled &&
          onRestart &&
          onPause &&
          onResume && (
            <LpRecordingStickyFooter
              position="bottom"
              elapsedLabel={elapsedLabel}
              isPaused={recordingState === 'paused'}
              isFinished={
                durationLimitReached ||
                elapsedTime >= COACHING_RECORDING_CONFIG.MAX_DURATION_MS
              }
              onRestart={() => setShowRestartConfirmation(true)}
              onPause={onPause}
              onResume={onResume}
              onSubmit={handleLessonCompleteClick}
            />
          )}

        {isRecordingEnabled &&
          recordingState === 'idle' &&
          !hasExistingRecording &&
          onRecordStart && (
            <LpRecordingIdleStickyFooter
              position="bottom"
              onRecordStart={onRecordStart}
            />
          )}

        {/* Show existing recording - ready to submit */}
        {isRecordingEnabled &&
          hasExistingRecording &&
          recordingState === 'idle' && (
            <LpRecordingStickyFooter
              position="bottom"
              elapsedLabel={existingRecordingDuration}
              isPaused={false}
              isFinished={true}
              onRestart={onRecordStart || (() => {})}
              onPause={() => {}}
              onResume={() => {}}
              onSubmit={onSubmitExistingRecording}
            />
          )}
      </Container>
    </>
  );
};

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: var(--rk-spacing-xl);
  padding-bottom: var(--rk-spacing-xxxxxxl);

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-md);
  }
`;

const HeaderContainer = styled.div`
  background: var(--md-sys-color-surface);
  border-radius: var(--rk-border-radius-lg);
  padding: var(--rk-spacing-xl);
  margin-bottom: var(--rk-spacing-lg);
  box-shadow: var(--rk-elevation-sm);
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const ContentContainer = styled.div<{ $dimmed?: boolean }>`
  background: var(--md-sys-color-surface);
  border-radius: var(--rk-border-radius-lg);
  margin-bottom: var(--rk-spacing-lg);
  box-shadow: var(--rk-elevation-sm);
  overflow: hidden;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.3 : 1)};
  pointer-events: ${({ $dimmed }) => ($dimmed ? 'none' : 'auto')};
  transition: opacity 0.3s ease;
`;

const AiProcessingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: var(--rk-border-radius-lg);
`;

const MetadataContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--rk-spacing-md);
  margin-bottom: var(--rk-spacing-lg);
`;

const TranslateContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-md);
  align-items: center;
  margin: var(--rk-spacing-xl) 0;
`;

const ActionButtonsResponsive = styled(StyledDiv)`
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
  }
`;

const EditButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;

  ${mq(BreakpointSizes.xs)} {
    width: 100%;

    & > button,
    & > md-outlined-button {
      width: 100%;
    }
  }
`;

const EditMenu = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 4px;
  background: var(--md-sys-color-surface);
  border-radius: var(--rk-border-radius-md);
  box-shadow: var(--rk-elevation-md);
  border: 1px solid var(--md-sys-color-outline-variant);
  z-index: 2100;
  min-width: 180px;
  overflow: hidden;
`;

const EditMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--md-sys-color-surface-variant);
  }
`;

const VersionBanner = styled.div<{ $isLatest: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  padding: var(--rk-spacing-sm) var(--rk-spacing-md) var(--rk-spacing-sm)
    var(--rk-spacing-lg);
  border-radius: 100px;
  flex-shrink: 0;
  background: transparent;
  cursor: pointer;
  border: 1.5px solid
    ${({ $isLatest }) =>
      $isLatest
        ? 'var(--md-sys-color-secondary)'
        : 'var(--md-sys-color-error)'};
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--md-sys-color-surface-variant);
  }
`;
